import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { requirementTagsApi } from '../services/api';
import type { CreateRequirementTagInput, UpdateRequirementTagInput } from '../types';

export function useRequirementTags() {
  return useQuery({
    queryKey: ['requirementTags'],
    queryFn: async () => {
      const response = await requirementTagsApi.getAll();
      return response.data;
    },
  });
}

export function useRequirementTag(id: number) {
  return useQuery({
    queryKey: ['requirementTags', id],
    queryFn: async () => {
      const response = await requirementTagsApi.getById(id);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateRequirementTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRequirementTagInput) => requirementTagsApi.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['requirementTags'] });
    },
  });
}

export function useUpdateRequirementTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateRequirementTagInput }) =>
      requirementTagsApi.update(id, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['requirementTags'] });
      void queryClient.invalidateQueries({ queryKey: ['requirementTags', id] });
    },
  });
}

export function useDeleteRequirementTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => requirementTagsApi.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['requirementTags'] });
    },
  });
}

// Patient requirements hooks

export function usePatientRequirements(patientId: number) {
  return useQuery({
    queryKey: ['patientRequirements', patientId],
    queryFn: async () => {
      const response = await requirementTagsApi.getPatientRequirements(patientId);
      return response.data;
    },
    enabled: !!patientId,
  });
}

export function useAddPatientRequirement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ patientId, tagId }: { patientId: number; tagId: number }) =>
      requirementTagsApi.addPatientRequirement(patientId, tagId),
    onSuccess: (_, { patientId }) => {
      void queryClient.invalidateQueries({ queryKey: ['patientRequirements', patientId] });
      void queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
  });
}

export function useRemovePatientRequirement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ patientId, tagId }: { patientId: number; tagId: number }) =>
      requirementTagsApi.removePatientRequirement(patientId, tagId),
    onSuccess: (_, { patientId }) => {
      void queryClient.invalidateQueries({ queryKey: ['patientRequirements', patientId] });
      void queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
  });
}
